<template>
  <AssessmentResultsLayout
    :variant="useNewShell ? 'new' : 'legacy'"
    :title="$t('assessmentResults.shellTitle')"
    :subtitle="$t('assessmentResults.shellSubtitle')"
  >
  <!-- 🔹 Общая оценка -->
  <section :class="useNewShell ? 'sv-card ar-section-metrics' : ''">
  <div class="team-info-card">
  <div class="info-block team">
    <h3>🏷️ {{ $t('assessmentResults.team') }}</h3>
    <p>{{ teamName || $t('assessmentResults.defaultTeam') }}</p>
  </div>
  <div class="info-block score" :class="scoreColor">
    <h3>📊 {{ $t('assessmentResults.score') }}</h3>
    <p>{{ averageScore.toFixed(2) }}</p>
  </div>
<div class="info-block level">
  <h3>
    🏅 {{ $t('assessmentResults.level') }}
    <span class="info-icon" @click="showLevelInfo = true" style="cursor: pointer;">ℹ️</span>
  </h3>
  <p>{{ teamLevel }}</p>
</div>

  <div class="info-block market">
  <h3>
    📈 {{ $t('assessmentResults.vsIndustry') }}
    <span class="info-icon" @click="showMarketModal = true">ℹ️</span>
  </h3>
  <p>
    <span :class="compareToMarket >= 0 ? 'better' : 'worse'">
      {{ compareToMarket >= 0 ? '+' : '' }}{{ compareToMarket.toFixed(2) }}%
    </span>
  </p>
  <small v-if="previousAverage !== null" class="previous-diff">
    {{ performanceChangeText }}
  </small>
</div>
</div>
</section>

<LevelInfoModal v-if="showLevelInfo" @close="showLevelInfo = false" />
<MarketInfoModal v-if="showMarketModal" @close="showMarketModal = false" />

<section v-if="timelineInfo" :class="useNewShell ? 'sv-card ar-section-timeline' : ''">
<div class="timeline-modern">
  <div class="timeline-track">
    <!-- 🔸 Заливка прогресса -->
    <div
      class="timeline-fill"
      :style="{ width: getTimelinePosition(todayDate) + '%' }"
    ></div>

    <!-- 📍 Прошлая точка -->
    <div
      class="timeline-dot"
      :style="{ left: getTimelinePosition(timelineInfo.lastDate) + '%' }"
    >
      <div class="tooltip always-visible">
        📍 {{ $t('assessmentResults.timelineLast', { date: timelineInfo.lastDate }) }}
      </div>
    </div>

    <!-- ➤ Сегодняшняя стрелка -->


    <!-- ⏭ Следующая точка -->
    <div
      class="timeline-dot"
      :style="{ left: getTimelinePosition(timelineInfo.nextDate) + '%' }"
    >
      <div class="tooltip always-visible">
        ⏭ {{ $t('assessmentResults.timelineNext', { date: timelineInfo.nextDate }) }}
      </div>
    </div>
  </div>

  <!-- 🧾 Подпись под шкалой -->
  <div class="timeline-days-left">
    ⏳ {{ $t('assessmentResults.daysUntil') }}
    <strong>{{ timelineInfo.daysLeft }} {{ pluralDays(timelineInfo.daysLeft) }}</strong>
  </div>
  <div v-if="previousAssessmentDates.length" class="timeline-previous-dates">
  📅 {{ $t('assessmentResults.previousAssessments') }}
  <strong>{{ previousAssessmentDates.join(', ') }}</strong>
</div>

</div>
</section>




    <!-- 🔹 Если пользователь не авторизован, показываем предложение зарегистрироваться -->
    <div v-if="!isAuthenticated" :class="['auth-notice', useNewShell ? 'auth-notice--shell' : '']">
      <p>{{ $t('assessmentResults.authNotice') }}</p>
      <button type="button" @click="goToRegister">🔐 {{ $t('assessmentResults.register') }}</button>
    </div>



    <!-- 🔹 Загрузка / Ошибка -->
    <div v-if="loading" class="loading">⏳ {{ $t('assessmentResults.loading') }}</div>
<div v-else-if="error" class="error">❌ {{ error }}</div>

<!-- 🔹 Графики по категориям -->
<div v-else class="charts-container" :class="{ 'charts-container--shell': useNewShell }">
  <RadarChart
    v-for="row in radarChartPanels"
    :key="row.key"
    :chartData="row.chartData"
    :title="row.title"
    class="radar-chart"
/>


</div>
<p v-if="hasRadarComparisons" class="radar-legend-hint">
  {{ $t('assessmentResults.radarLegendHint') }}
</p>

<div v-if="showAssessmentPersistenceTools" :class="useNewShell ? 'sv-card ar-persist-wrap ar-persist-hub' : 'ar-persist-hub'">
  <header class="ar-persist-hub__head">
    <h3 class="ar-persist-hub__title">{{ $t('assessmentResults.persistHubTitle') }}</h3>
    <p class="ar-persist-hub__subtitle">{{ $t('assessmentResults.persistHubSubtitle') }}</p>
  </header>

  <section class="ar-plan-section improvement-plan-block">
    <h4 class="ar-persist-hub__step">{{ $t('assessmentResults.persistPlanStep') }}</h4>
    <button
      type="button"
      @click="generateImprovementPlan"
      :disabled="loadingPlan"
      class="ar-action-btn ar-action-btn--primary"
    >
      {{ $t('assessmentResults.generatePlan') }}
    </button>

    <div v-if="loadingPlan" class="ar-persist-status">⏳ {{ $t('assessmentResults.generatingPlan') }}</div>

    <div v-if="editablePlan.length" class="plan-editable">
      <ul class="ar-plan-list ar-plan-list--editable">
        <li v-for="(item, index) in editablePlan" :key="'ed-' + index" class="ar-plan-item ar-plan-item--editable">
          <input type="checkbox" v-model="item.done" class="ar-plan-check" />
          <textarea
            v-model="item.text"
            class="editable-input ar-plan-textarea"
            :class="{ completed: item.done }"
            rows="4"
            @input="autoResize($event)"
          ></textarea>
          <button type="button" class="ar-plan-remove" @click="removeStep(index)" :aria-label="$t('common.close')">×</button>
        </li>
      </ul>

      <div class="plan-buttons ar-persist-toolbar">
        <button type="button" class="ar-action-btn ar-action-btn--ghost" @click="addStep">
          {{ $t('assessmentResults.addStep') }}
        </button>
        <button type="button" class="ar-action-btn ar-action-btn--success" @click="saveImprovementPlan">
          {{ $t('assessmentResults.save') }}
        </button>
      </div>
    </div>

    <div v-else-if="savedPlan.length">
      <ul class="plan-cards ar-plan-list">
        <li
          v-for="(item, index) in savedPlan"
          :key="'sv-' + index"
          class="plan-card ar-plan-item"
          @click="handleCardClick($event)"
        >
          <input
            type="checkbox"
            v-model="item.done"
            class="ar-plan-check"
            @change="saveImprovementPlan"
            @click.stop
          />
          <span class="ar-plan-text" :class="{ completed: item.done }">{{ item.text }}</span>
        </li>
      </ul>
    </div>
  </section>

<div class="ar-persist-divider"></div>

  <section class="ar-rec-section recommendations-block">
    <h4 class="ar-persist-hub__step">{{ $t('assessmentResults.persistRecsStep') }}</h4>
    <button type="button" @click="fetchOpenAIRecommendations" class="ar-action-btn ar-action-btn--primary ar-action-btn--secondary-cta">
      {{ $t('assessmentResults.getRecommendations') }}
    </button>

    <div v-if="loadingDetailedRecs" class="ar-persist-status">⏳ {{ $t('assessmentResults.analyzing') }}</div>

    <div v-if="recommendationsHtml" v-html="recommendationsHtml" class="recommendation-html ar-rec-body"></div>
    <div v-if="recommendationsHtml" class="ar-persist-toolbar ar-persist-toolbar--right">
      <button type="button" @click="saveRecommendations" class="ar-action-btn ar-action-btn--success">
        {{ $t('assessmentResults.saveRecommendations') }}
      </button>
    </div>
  </section>
</div>

<section
  v-if="!loading && !error && rankedTrainingPrograms.length"
  class="trn-rec trn-rec--footer"
>
  <header class="trn-rec__head">
    <h3 class="trn-rec__title">{{ $t('assessmentResults.icagileTitle') }}</h3>
    <p class="trn-rec__lead">{{ trainingRecommendationsIntro }}</p>
  </header>

  <div class="trn-rec__grid">
    <article
      v-for="prog in rankedTrainingPrograms"
      :key="prog.id"
      class="trn-card"
    >
      <header class="trn-card__head">
        <span class="trn-card__online">{{ $t('assessmentResults.onlineOnly') }}</span>
        <h4 class="trn-card__title">{{ trainingProgramTitle(prog) }}</h4>
        <p class="trn-card__pitch">{{ programPitch(prog) }}</p>
      </header>

      <ul class="trn-card__outcomes">
        <li v-for="(item, idx) in programOutcomes(prog)" :key="prog.id + '-out-' + idx">
          {{ item }}
        </li>
      </ul>

      <div class="trn-card__meta">
        <div class="trn-card__meta-row">
          <span class="trn-card__meta-label">{{ $t('assessmentResults.nextDatesLabel') }}</span>
          <strong class="trn-card__meta-val">{{ trainingProgramDatesLabel(prog) }}</strong>
        </div>
        <div v-if="programDisplayPrice(prog)" class="trn-card__meta-row">
          <span class="trn-card__meta-label">{{ $t('assessmentResults.priceLabel') }}</span>
          <strong class="trn-card__meta-val">{{ formatTrainingPricesLine(programDisplayPrice(prog)) }}</strong>
        </div>
      </div>

      <div v-if="fitLoadingById[prog.id]" class="trn-card__fit trn-card__fit--loading">
        {{ $t('assessmentResults.programFitLoading') }}
      </div>
      <div v-else-if="programFitById[prog.id]" class="trn-card__fit">
        <strong class="trn-card__fit-label">{{ $t('assessmentResults.whyFitLabel') }}</strong>
        <p class="trn-card__fit-body">{{ programFitById[prog.id] }}</p>
      </div>

      <div class="yes-no trn-card__actions">
        <button
          type="button"
          class="btn-answer btn-yes active"
          @click="prefillTrainingLead(prog)"
        >
          {{ $t('assessmentResults.learnMoreBtn') }}
        </button>
        <a
          class="btn-answer btn-no active"
          :href="telegramContactHref"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ $t('assessmentResults.telegramCta') }}
        </a>
      </div>
    </article>
  </div>

  <p class="trn-rec__hint">{{ $t('assessmentResults.programDatesHint') }}</p>
</section>

<div v-if="showLeadModal" class="lead-modal-overlay" @click.self="closeLeadModal">
  <div class="lead-modal" role="dialog" aria-modal="true" aria-labelledby="lead-modal-title">
    <button
      type="button"
      class="modal-close-top"
      @click="closeLeadModal"
      :aria-label="$t('common.close')"
    >✕</button>
    <h3 id="lead-modal-title" class="lead-modal__title">{{ $t('assessmentResults.learnMoreBtn') }}</h3>
    <p class="lead-modal__hint">{{ $t('assessmentResults.leadModalHint') }}</p>
    <p v-if="leadModalProgramTitle" class="lead-modal__program">
      <span class="lead-modal__program-label">{{ $t('assessmentResults.leadModalProgramLabel') }}</span>
      <strong>{{ leadModalProgramTitle }}</strong>
    </p>
    <form class="lead-modal__form" @submit.prevent="submitTrainingLead">
      <label class="lead-modal__row">
        <span class="lead-modal__label">Email</span>
        <input
          v-model.trim="leadForm.email"
          type="email"
          required
          placeholder="you@company.com"
          class="lead-modal__input"
        />
      </label>
      <div class="lead-modal__actions">
        <button
          type="button"
          class="lead-modal__btn lead-modal__btn--ghost"
          @click="closeLeadModal"
        >
          {{ $t('common.close') }}
        </button>
        <button
          type="submit"
          class="lead-modal__btn lead-modal__btn--primary"
          :disabled="leadSubmitting"
        >
          {{ leadSubmitting ? $t('assessmentResults.sending') : $t('assessmentResults.sendLeadBtn') }}
        </button>
      </div>
      <p v-if="leadStatus" class="lead-modal__status">{{ leadStatus }}</p>
    </form>
  </div>
</div>
</AssessmentResultsLayout>
</template>

<script>
import axios from "axios";
import RadarChart from "@/components/RadarChart.vue";
import LevelInfoModal from "@/components/LevelInfoModal.vue";
import MarketInfoModal from "@/components/MarketInfoModal.vue";
import AssessmentResultsLayout from "@/components/AssessmentResultsLayout.vue";
import enLocale from "@/i18n/locales/en.json";
import i18n from "@/i18n";
import { pickIcagileProgramsForResults, ICAGILE_FX_FROM_EUR } from "@/data/icagile-training-catalog";

/** Должен совпадать с UserSurvey.vue — кэш тела ответа POST /temp_results */
const GUEST_SURVEY_RESULTS_STORAGE_KEY = "growboard_guest_survey_results_v1";
export default {
  components: { RadarChart, LevelInfoModal, MarketInfoModal, AssessmentResultsLayout },
  props: ["team_id", "assessment_id"],

  data() {
    return {
      showLevelInfo: false,
      showMarketModal: false,
      results: {},
      radarData: {},
      loading: true,
      error: null,
      averageScore: 0,
      isAuthenticated: false, // Проверка авторизации
      recommendationsDetailed: [],
      loadingDetailedRecs: false,
      recommendationsHtml: "",
      chartInstance: null,
      isUnmounted: false, // 🔒 добавляем
      timelineInfo: null,
      resultsHistory: {},
      hoveredPoint: null,
      teamName: "", // можно получить через props или API
    previousAverage: null, // если сравниваем с прошлым
       loadingPlan: false,
    editablePlan: [],
    savedPlan: [],
    editing: false,
    maturityTeamTypeFromApi: null,
      onlineProgramsMetaById: {},
      leadSubmitting: false,
      leadStatus: "",
      showLeadModal: false,
      leadForm: {
        programId: "",
        email: "",
      },
      programFitById: {},
      fitLoadingById: {},

    };
  },

  methods: {
  async fetchOpenAIRecommendations() {
  if (!this.savedPlan || this.savedPlan.length === 0) {
    alert(this.$t("assessmentResults.alertNeedPlan"));
    return;
  }

  this.loadingDetailedRecs = true;

  try {
    const response = await axios.post("/openai_recommend", {
      plan: this.savedPlan,
      assessment_id: this.assessmentId
    });

    console.log("📨 Ответ от OpenAI:", response.data.content);
    const htmlFormatted = response.data.content;
    this.recommendationsHtml = `<p>${htmlFormatted}</p>`;
  } catch (error) {
    console.error("❌ Ошибка при получении рекомендаций:", error.response?.data || error);
    alert(this.$t("assessmentResults.alertRecError"));
  } finally {
    this.loadingDetailedRecs = false;
  }
},
    async saveRecommendations() {
  try {
    const token = localStorage.getItem("token");

    if (!token) {
      console.warn("🚫 Нет токена авторизации.");
      alert(this.$t("assessmentResults.alertNotAuth"));
      return;
    }

    await axios.post(
      `/assessment/${this.assessment_id}/recommendations`,
      {
        recommendations: this.recommendationsHtml,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    alert(this.$t("assessmentResults.alertRecSaved"));
  } catch (error) {
    console.error("❌ Ошибка сохранения:", error.response?.data || error);
    alert(this.$t("assessmentResults.alertRecSaveFail"));
  }
},

    async generateImprovementPlan() {
  this.loadingPlan = true;
  try {
    const res = await axios.post("/generate_plan", {
      assessment_id: this.assessment_id,
      answer_text: this.prepareFullAnswerSummary()
    });

    const rawSteps = res.data.steps || [];

    // 🔥 Вот здесь магия!
    this.editablePlan = rawSteps.map(step =>
      typeof step === "string" ? { text: step, done: false } : step
    );

    this.editing = true;
  } catch (err) {
    alert(this.$t("assessmentResults.alertPlanGenFail"));
    console.error(err);
  } finally {
    this.loadingPlan = false;
  }
},
  saveImprovementPlan() {
  const token = localStorage.getItem("token");

  const planToSave = this.editing ? this.editablePlan : this.savedPlan;

  axios
    .post(
      `/assessment/${this.assessment_id}/plan`,
      { plan: planToSave },
      { headers: { Authorization: `Bearer ${token}` } }
    )
    .then(() => {
      // 💾 Только в режиме редактирования обновляем savedPlan
      if (this.editing) {
        this.savedPlan = JSON.parse(JSON.stringify(this.editablePlan));
        this.editablePlan = [];
        this.editing = false;
      }

    })
    .catch((err) => {
      console.error("Ошибка сохранения плана", err);
      alert("❌ Не удалось сохранить план");
    });
}
,
  editPlan() {
    this.editablePlan = JSON.parse(JSON.stringify(this.savedPlan));
    this.editing = true;
  },
  addStep() {
    this.editablePlan.push({ text: "", done: false });
  },
  removeStep(index) {
    this.editablePlan.splice(index, 1);
  },
  async fetchSavedPlan() {
    const token = localStorage.getItem("token");
    const res = await axios.get(
      `/assessment/${this.assessment_id}/plan`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    this.savedPlan = res.data.plan || [];
  },

    async fetchSavedRecommendations() {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      console.warn("🚫 Нет токена авторизации.");
      return;
    }

    const res = await axios.get(
      `/assessment/${this.assessment_id}/recommendations`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    if (res.data && res.data.recommendations) {
      this.recommendationsHtml = `<p>${res.data.recommendations}</p>`;
      console.log("📥 Подгружены сохранённые рекомендации");
    } else {
      console.log("ℹ️ Нет сохранённых рекомендаций");
    }
  } catch (error) {
    console.error("❌ Ошибка при загрузке рекомендаций:", error.response?.data || error);
  }
},
async fetchResultsHistory() {
  try {
    const token = localStorage.getItem("token");

        const res = await axios.get(
      `/team_results_history/${this.team_id}`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        params: {
          lang: this.currentUiLocale,
          ...(this.resultsTeamTypeParam ? { team_type: this.resultsTeamTypeParam } : {}),
        },
      }
    );

    const history = res.data;
    this.resultsHistory = history;
    console.log("📜 История оценок:", history);

    const sortedDates = Object.keys(history).sort().reverse();


    if (sortedDates.length > 0) {
  this.timelineInfo = this.getNextAssessmentInfo(sortedDates[0]); // <-- Добавь эту строчку
}

if (sortedDates.length >= 2) {
  this.buildCombinedRadarDataByCategory(history);
  this.calculateAverageFromLatestSession(history);
} else if (sortedDates.length === 1) {
  this.prepareRadarData();
} else {
  this.error = this.$t("assessmentResults.errPassFirst");
}

  } catch (error) {
    console.error("❌ Ошибка при получении истории:", error.response?.data || error);
    this.error = this.$t("assessmentResults.errHistory");
  }
},


    async fetchResults() {
      try {
        console.log(`📡 Загружаем результаты для команды ID ${this.team_id}`);
        const token = localStorage.getItem("token");
        this.isAuthenticated = !!token;

        const params = {
          lang: this.currentUiLocale,
          ...(this.resultsTeamTypeParam ? { team_type: this.resultsTeamTypeParam } : {}),
        };
        const aid = Number(this.assessment_id);
        if (Number.isFinite(aid) && aid > 0) {
          params.for_assessment_id = aid;
        }

        if (!token) {
          if (String(this.team_id) === "0") {
            try {
              const raw = sessionStorage.getItem(GUEST_SURVEY_RESULTS_STORAGE_KEY);
              if (raw) {
                const parsed = JSON.parse(raw);
                if (parsed?.results && typeof parsed.results === "object") {
                  console.log("✅ Гостевые результаты из sessionStorage");
                  this.applyResultsPayload(parsed);
                  return;
                }
              }
            } catch (e) {
              console.warn("guest sessionStorage read failed", e);
            }
          }
          const res = await axios.get("/temp_results", { params });
          this.applyResultsPayload(res.data);
          return;
        }

        const res = await axios.get(`/team_results/${this.team_id}`, {
          headers: { Authorization: `Bearer ${token}` },
          params,
        });
        this.applyResultsPayload(res.data);
      } catch (error) {
        console.error("❌ Ошибка загрузки данных:", error.response?.data || error);
        this.error = this.$t("assessmentResults.errLoad");
      } finally {
        this.loading = false;
      }
    },

    applyResultsPayload(data) {
      if (!data?.results || typeof data.results !== "object") {
        throw new Error("Invalid results payload");
      }
      console.log("✅ Данные загружены:", data);
      this.teamName = data.team_name;
      this.results = data.results;
      this.maturityTeamTypeFromApi = data.maturity_team_type || null;
      this.prepareRadarData();
    },
    autoResize(event) {
  const el = event.target;
  el.style.height = "auto";         // сброс высоты
  el.style.height = el.scrollHeight + "px"; // установить по содержимому
},
handleCardClick(event) {
  const isCheckbox = event.target.tagName === "INPUT";
  if (!isCheckbox) {
    this.editPlan();
  }
},
   isChartDataValid(chartData) {
  return (
    chartData &&
    Array.isArray(chartData.labels) &&
    chartData.labels.length > 0 &&
    Array.isArray(chartData.datasets) &&
    chartData.datasets.length > 0
  );
},
    getTimelinePosition(date) {
  const start = new Date(this.timelineInfo.lastDate);
  const end = this.timelineInfo.nextDateObject;
  const current = new Date(date);

  const total = end - start;
  const elapsed = current - start;

  const raw = (elapsed / total) * 100;

  // 🔄 Сжимаем расстояние между точками: вместо 0-100% будет 30%-70%
  const compressed = 30 + (raw * 0.4);

  return Math.min(100, Math.max(0, compressed)).toFixed(1);
},

    getNextAssessmentInfo(lastDateStr) {
  const lastDate = new Date(lastDateStr);
  const today = new Date();

  // Добавляем 10 недель
  let nextDate = new Date(lastDate);
  nextDate.setDate(nextDate.getDate() + 70);

  // Если это выходной, переносим на ближайший будний
  while (nextDate.getDay() === 0 || nextDate.getDay() === 6) {
    nextDate.setDate(nextDate.getDate() + 1);
  }

  const daysLeft = Math.ceil((nextDate - today) / (1000 * 60 * 60 * 24));

  return {
    lastDate: lastDate.toLocaleDateString(),
    nextDate: nextDate.toLocaleDateString(),
    daysLeft,
    nextDateObject: nextDate,
  };
},

calculateAverageFromLatestSession(history) {
  const sortedDates = Object.keys(history).sort().reverse();
  const latestSession = history[sortedDates[0]];

  if (!latestSession) {
    console.warn("⚠️ Нет данных для последней сессии.");
    this.averageScore = 0;
    return;
  }

  let total = 0;
  let count = 0;

  for (const categoryData of Object.values(latestSession)) {
    for (const score of Object.values(categoryData)) {
      total += score;
      count++;
    }
  }

  this.averageScore = count > 0 ? total / count : 0;
},
    processHistoryRadarData(history) {
  const sortedDates = Object.keys(history).sort().reverse(); // Последняя оценка — первая

  if (sortedDates.length === 0) return;

  const allLabels = new Set();
  const datasets = [];

  sortedDates.slice(0, 2).forEach((date, index) => {
    const session = history[date];
    const dataMap = {};

    for (const [category, subcats] of Object.entries(session)) {
      for (const [subcategory, score] of Object.entries(subcats)) {
        const label = `${category} - ${subcategory}`;
        dataMap[label] = score;
        allLabels.add(label);
      }
    }

    const dataset = {
      label: index === 0 ? this.$t("assessmentResults.radarLatest") : this.$t("assessmentResults.radarPrevious"),
      data: [],
      backgroundColor: index === 0 ? "rgba(75, 192, 192, 0.2)" : "rgba(255, 206, 86, 0.2)",
      borderColor: index === 0 ? "rgba(75, 192, 192, 1)" : "rgba(255, 206, 86, 1)",
      borderWidth: 2,
    };

    allLabels.forEach(label => {
      dataset.data.push(dataMap[label] || 0); // если подкатегория отсутствует в этой сессии — 0
    });

    datasets.push(dataset);
  });

  this.radarData = {
    labels: Array.from(allLabels),
    datasets,
  };
},
    buildCombinedRadarData(latestResults, history) {
  const sortedDates = Object.keys(history).sort().reverse(); // самые свежие сначала
  const allLabels = new Set();
  const labelToScoreMap = {};

  // 🔹 Обрабатываем последние 2 измерения (в том числе текущее)
  sortedDates.slice(0, 2).forEach(date => {
  const session = history[date];
  const dataMap = {};

  for (const [category, subcats] of Object.entries(session)) {
    for (const [subcategory, score] of Object.entries(subcats)) {
      const label = `${category} - ${subcategory}`;
      dataMap[label] = score;
      allLabels.add(label);
    }
  }

  labelToScoreMap[date] = dataMap;
});

  const labels = Array.from(allLabels);
  const datasets = [];

  sortedDates.slice(0, 2).forEach((date, index) => {
    const dataMap = labelToScoreMap[date];
    const dataset = {
      label: index === 0 ? this.$t("assessmentResults.radarLatest") : this.$t("assessmentResults.radarPrevious"),
      data: labels.map(label => dataMap[label] || 0),
      backgroundColor: index === 0 ? "rgba(75, 192, 192, 0.2)" : "rgba(255, 206, 86, 0.2)",
      borderColor: index === 0 ? "rgba(75, 192, 192, 1)" : "rgba(255, 206, 86, 1)",
      borderWidth: 2,
    };
    datasets.push(dataset);
  });

  this.radarData = { labels, datasets };
},
buildCombinedRadarDataByCategory(history) {
  const sortedDates = Object.keys(history).sort().reverse();
  if (sortedDates.length === 0) {
    this.error = this.$t("assessmentResults.errProgress");
    return;
  }

  const latestSession = history[sortedDates[0]];
  const previousSession = sortedDates[1] ? history[sortedDates[1]] : null;

  const radarDataByCategory = {};

  // Проходим по категориям последней оценки
  for (const [category, subcats] of Object.entries(latestSession)) {
    const labels = Object.keys(subcats);

    const latestData = labels.map(label => latestSession[category][label] || 0);
    const previousData = labels.map(label => previousSession?.[category]?.[label] || 0);

    radarDataByCategory[category] = {
      labels,
      datasets: [
        {
          label: this.$t("assessmentResults.radarLatest"),
          data: latestData,
          backgroundColor: "rgba(75, 192, 192, 0.2)",
          borderColor: "rgba(75, 192, 192, 1)",
          borderWidth: 2
        },
        {
          label: this.$t("assessmentResults.radarPrevious"),
          data: previousData,
          backgroundColor: "rgba(255, 206, 86, 0.2)",
          borderColor: "rgba(255, 206, 86, 1)",
          borderWidth: 2
        }
      ]
    };
  }

  this.radarData = radarDataByCategory;
},

pluralDays(n) {
  const abs = Math.abs(n);
  const loc = typeof this.$i18n.locale === "string" ? this.$i18n.locale : this.$i18n.locale.value;
  if (loc === "en") return abs === 1 ? this.$t("assessmentResults.dayOne") : this.$t("assessmentResults.dayMany");
  if (abs % 10 === 1 && abs % 100 !== 11) return "день";
  if ([2, 3, 4].includes(abs % 10) && ![12, 13, 14].includes(abs % 100)) return "дня";
  return "дней";
},

    prepareRadarData() {
      let totalScore = 0;
      let categoryCount = 0;

      this.radarData = Object.entries(this.results).reduce((acc, [category, scores]) => {
        if (!scores || Object.keys(scores).length === 0) {
          console.warn(`⚠️ Категория "${category}" пуста, пропускаем.`);
          return acc;
        }

        console.log(`📌 Формируем данные для категории: ${category}`);

        const scoresArray = Object.values(scores).map(value => parseFloat(value) || 0);
        const categoryAverage = scoresArray.reduce((sum, val) => sum + val, 0) / scoresArray.length;

        totalScore += categoryAverage;
        categoryCount++;

        acc[category] = {
          labels: Object.keys(scores),
          datasets: [
            {
              label: category,
              data: scoresArray,
              backgroundColor: "rgba(75, 192, 192, 0.2)",
              borderColor: "rgba(75, 192, 192, 1)",
              borderWidth: 2
            }
          ]
        };
        return acc;
      }, {});

      this.averageScore = categoryCount > 0 ? totalScore / categoryCount : 0;

      console.log("📊 Итоговые данные для диаграмм:", JSON.stringify(this.radarData, null, 2));
    },
    prepareFullAnswerSummary() {
  let summary = "";
  for (const [category, questions] of Object.entries(this.results)) {
    summary += `📌 Категория: ${category}\n`;
    for (const [question, answer] of Object.entries(questions)) {
      summary += ` - ${question}: ${answer}\n`;
    }
    summary += "\n";
  }
  return summary;
  },

    goToRegister() {
      this.$router.push({
        name: "Register",
        query: { next: this.$route.fullPath },
      });
    },

    trainingProgramTitle(prog) {
      return this.isEnglishUI() ? prog.titleEn : prog.titleRu;
    },

    programPitch(prog) {
      if (!prog) return "";
      return this.isEnglishUI() ? prog.pitchEn || prog.pitchRu : prog.pitchRu || prog.pitchEn;
    },

    programOutcomes(prog) {
      if (!prog) return [];
      const en = Array.isArray(prog.outcomesEn) ? prog.outcomesEn : [];
      const ru = Array.isArray(prog.outcomesRu) ? prog.outcomesRu : [];
      const list = this.isEnglishUI() ? (en.length ? en : ru) : (ru.length ? ru : en);
      return list.slice(0, 4);
    },

    programDisplayPrice(prog) {
      const meta = this.getProgramMetaById(prog?.id);
      const adminPrice = Number(meta?.base_price_eur || 0);
      if (adminPrice > 0) return adminPrice;
      return Number(prog?.basePriceEur || 0);
    },

    async fetchOnlineProgramsMeta() {
      try {
        const res = await axios.get("/api/online-programs");
        const map = {};
        for (const row of res.data?.programs || []) {
          if (!row?.program_id) continue;
          map[row.program_id] = row;
        }
        this.onlineProgramsMetaById = map;
      } catch (_e) {
        this.onlineProgramsMetaById = {};
      }
    },

    getProgramMetaById(programId) {
      return this.onlineProgramsMetaById?.[programId] || null;
    },

    trainingProgramDatesLabel(prog) {
      const meta = this.getProgramMetaById(prog.id);
      const dates = Array.isArray(meta?.next_dates) ? meta.next_dates : [];
      if (!dates.length) return this.$t("assessmentResults.datesOnRequest");
      return dates.join(", ");
    },

    programBookingUrl(prog) {
      const meta = this.getProgramMetaById(prog.id);
      return meta?.booking_url || prog.url;
    },

    prefillTrainingLead(prog) {
      this.leadForm.programId = prog.id;
      this.leadStatus = "";
      this.showLeadModal = true;
    },

    closeLeadModal() {
      this.showLeadModal = false;
    },

    weakDomainsForPrograms() {
      return Object.entries(this.results || {})
        .filter(([, subs]) => subs && typeof subs === "object" && Object.keys(subs).length > 0)
        .map(([cat, subs]) => {
          const values = Object.values(subs).map((v) => Number(v) || 0);
          const avg = values.reduce((a, b) => a + b, 0) / Math.max(1, values.length);
          return { cat, avg };
        })
        .sort((a, b) => a.avg - b.avg)
        .slice(0, 3)
        .map((x) => this.radarCategoryTitle(x.cat));
    },

    async explainProgramFit(prog) {
      if (!prog?.id) return;
      if (this.programFitById[prog.id]) return;
      if (this.fitLoadingById[prog.id]) return;
      this.fitLoadingById = { ...this.fitLoadingById, [prog.id]: true };
      try {
        const res = await axios.post("/api/training-program/explain-fit", {
          program_title: this.trainingProgramTitle(prog),
          weak_domains: this.weakDomainsForPrograms(),
        });
        this.programFitById = {
          ...this.programFitById,
          [prog.id]: String(res?.data?.explanation || "").trim(),
        };
      } catch (_e) {
        this.programFitById = {
          ...this.programFitById,
          [prog.id]: this.$t("assessmentResults.programFitError"),
        };
      } finally {
        this.fitLoadingById = { ...this.fitLoadingById, [prog.id]: false };
      }
    },
    async ensureProgramFitExplanations() {
      const items = this.rankedTrainingPrograms || [];
      for (const prog of items) {
        // sequential to avoid request bursts
        // eslint-disable-next-line no-await-in-loop
        await this.explainProgramFit(prog);
      }
    },

    async submitTrainingLead() {
      this.leadSubmitting = true;
      this.leadStatus = "";
      try {
        const selected = this.rankedTrainingPrograms.find((p) => p.id === this.leadForm.programId);
        await axios.post("/api/training-leads", {
          program_id: this.leadForm.programId || selected?.id || null,
          program_title: selected ? this.trainingProgramTitle(selected) : null,
          email: this.leadForm.email,
          message: this.$t("assessmentResults.defaultLeadMessage"),
          source_path: this.$route?.path || "/new/assessment-results",
        });
        this.leadStatus = this.$t("assessmentResults.leadSentOk");
        setTimeout(() => {
          this.showLeadModal = false;
        }, 1500);
      } catch (e) {
        this.leadStatus = e?.response?.data?.error || this.$t("assessmentResults.leadSentFail");
      } finally {
        this.leadSubmitting = false;
      }
    },

    async prefillLeadEmailFromProfile() {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const res = await axios.get("/api/user_profile", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const email = String(res.data?.username || "").trim();
        if (email && !this.leadForm.email) this.leadForm.email = email;
      } catch (_e) {
        /* no-op */
      }
    },

    formatTrainingPricesLine(eur) {
      const e = Number(eur) || 0;
      if (e <= 0) return "";
      const usd = e * ICAGILE_FX_FROM_EUR.USD;
      const rub = e * ICAGILE_FX_FROM_EUR.RUB;
      const loc = this.isEnglishUI() ? "en-US" : "ru-RU";
      const fmt = (n, cur) =>
        new Intl.NumberFormat(loc, { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(n);
      return `${fmt(e, "EUR")} · ${fmt(usd, "USD")} · ${fmt(rub, "RUB")}`;
    },

    isEnglishUI() {
      return String(this.currentUiLocale || "").toLowerCase().startsWith("en");
    },

    resolveRadarLabel(text) {
      if (text == null || text === "") return text;
      if (!this.isEnglishUI()) return text;
      const key = typeof text === "string" ? text.trim() : text;
      const map = this.enRadarLabelsMap;
      const direct = map[key];
      if (direct !== undefined && direct !== "") return direct;
      const candidates = Object.keys(map).sort((a, b) => b.length - a.length);
      for (const ru of candidates) {
        if (key.startsWith(ru) || ru.startsWith(key)) {
          const m = map[ru];
          if (m !== undefined && m !== "") return m;
        }
      }
      return text;
    },

    radarCategoryTitle(category) {
      return this.resolveRadarLabel(category);
    },

    localizeChartData(data) {
      if (!data || !Array.isArray(data.labels)) return data;
      const cloneDatasets = (datasets) =>
        (datasets || []).map((ds) => ({
          ...ds,
          data: Array.isArray(ds.data) ? ds.data.map((v) => Number(v)) : ds.data,
        }));
      /* Всегда отдаём копию: иначе для RU тот же reactive-объект, что в radarData —
       vue-chartjs (deep watch) + мутации Chart.js снова цепляются к Vue и дают петлю / пустой canvas. */
      if (!this.isEnglishUI()) {
        return {
          labels: [...data.labels],
          datasets: cloneDatasets(data.datasets),
        };
      }
      const tr = (label) => this.resolveRadarLabel(label);
      return {
        labels: data.labels.map(tr),
        datasets: (data.datasets || []).map((ds) => ({
          ...ds,
          label: tr(ds.label),
          data: Array.isArray(ds.data) ? ds.data.map((v) => Number(v)) : ds.data,
        })),
      };
    },
  },

  async mounted() {
    this.$nextTick(() => {
      try { window.scrollTo({ top: 0, behavior: "auto" }); } catch (_) { /* noop */ }
    });
    await this.fetchResults();
    await this.fetchOnlineProgramsMeta();
    await this.prefillLeadEmailFromProfile();
    await this.ensureProgramFitExplanations();
    if (this.showAssessmentPersistenceTools) {
      this.fetchSavedRecommendations();
      await this.fetchResultsHistory();
      this.fetchSavedPlan();
    }
  },
  computed: {
    useNewShell() {
      const p = this.$route && this.$route.path;
      return typeof p === "string" && p.startsWith("/new");
    },
    icagileMailtoHref() {
      const subj = encodeURIComponent(this.$t("assessmentResults.icagileMailSubject"));
      return `mailto:artjoms.grinakins@gmail.com?subject=${subj}`;
    },
    telegramContactHref() {
      return "https://t.me/agrinakins";
    },
    rankedTrainingPrograms() {
      if (this.loading || this.error || !this.results || typeof this.results !== "object") {
        return [];
      }
      if (!Object.keys(this.results).length) return [];
      return pickIcagileProgramsForResults(this.results, {
        maxPrograms: 2,
        minPrograms: 2,
      });
    },
    rankedTrainingProgramIds() {
      return this.rankedTrainingPrograms.map((p) => p.id).join(",");
    },
    weakDomainsJoinedForTraining() {
      const w = this.weakDomainsForPrograms();
      return w.length ? w.join(", ") : this.$t("assessmentResults.trainingWeakBalanced");
    },
    trainingRecommendationsIntro() {
      if (this.loading || this.error || !this.results || typeof this.results !== "object") {
        return "";
      }
      if (!Object.keys(this.results).length) return "";
      return this.$t("assessmentResults.trainingIntro", {
        score: this.averageScore.toFixed(2),
        level: this.teamLevel,
        weakAreas: this.weakDomainsJoinedForTraining,
      });
    },
    leadModalProgram() {
      const id = this.leadForm.programId;
      if (!id) return null;
      return this.rankedTrainingPrograms.find((p) => p.id === id) || null;
    },
    leadModalProgramTitle() {
      const p = this.leadModalProgram;
      if (!p) return "";
      return this.isEnglishUI() ? p.titleEn : p.titleRu;
    },
    currentUiLocale() {
      const g = i18n.global.locale;
      const fromGlobal = typeof g === "string" ? g : g?.value;
      if (fromGlobal && String(fromGlobal).length) {
        return String(fromGlobal);
      }
      let stored = null;
      try {
        stored = typeof localStorage !== "undefined" ? localStorage.getItem("language") : null;
      } catch (_e) {
        stored = null;
      }
      if (stored === "en" || stored === "ru") return stored;
      const l = this.$i18n?.locale;
      if (typeof l === "string") return l;
      if (l && typeof l === "object" && "value" in l && l.value) return String(l.value);
      return "ru";
    },
    /** Query после опроса, иначе тип с последнего GET /team_results (для осей радара по домену) */
    resultsTeamTypeParam() {
      const q = this.$route?.query?.team_type;
      if (q != null && String(q).trim() !== "") return String(q).trim();
      const api = this.maturityTeamTypeFromApi;
      if (api != null && String(api).trim() !== "") return String(api).trim();
      return null;
    },
    enRadarLabelsMap() {
      return enLocale.assessmentResults?.radarLabels || {};
    },
    /** План, рекомендации и история — только для сохранённой оценки (не гостевой session). */
    showAssessmentPersistenceTools() {
      const token = typeof localStorage !== "undefined" ? localStorage.getItem("token") : null;
      if (!token) return false;
      const tid = Number(this.team_id);
      const aid = Number(this.assessment_id);
      return tid > 0 && aid > 0;
    },
    teamLevel() {
    const score = this.averageScore;

    if (score < 2) return this.$t("assessmentResults.levelBeginner");
    if (score < 3) return this.$t("assessmentResults.levelGrowing");
    if (score < 4) return this.$t("assessmentResults.levelProgressing");
    return this.$t("assessmentResults.levelHigh");
  },
    previousAssessmentDates() {
  const dates = Object.keys(this.resultsHistory).sort().reverse();
  return dates.slice(1); // убираем самую последнюю (она уже отображается в точке)
},
  compareToMarket() {
    const marketAvg = 3.75;
    return ((this.averageScore - marketAvg) / marketAvg) * 100;
  },
  performanceChangeText() {
    if (this.previousAverage === null) return "";
    const delta = this.averageScore - this.previousAverage;
    const percent = Math.abs((delta / this.previousAverage) * 100).toFixed(1);
    return delta >= 0
      ? this.$t("assessmentResults.perfUp", { percent })
      : this.$t("assessmentResults.perfDown", { percent });
  },
  scoreColor() {
    const s = this.averageScore;
    if (s < 2) return "danger";
    if (s < 3) return "warning";
    if (s < 4) return "info";
    return "success";
  },
  filteredRadarData() {
    return Object.entries(this.radarData).reduce((acc, [category, chartData]) => {
      if (this.isChartDataValid(chartData)) {
        acc[category] = chartData;
      }
      return acc;
    }, {});
  },
  /** Стабильный список панелей: одна точка для localizeChartData, без вызова метода из шаблона на каждый tick */
  radarChartPanels() {
    return Object.entries(this.filteredRadarData).map(([category, data]) => ({
      key: `${category}-${this.currentUiLocale}`,
      category,
      title: this.radarCategoryTitle(category),
      chartData: this.localizeChartData(data),
    }));
  },
  hasRadarComparisons() {
    return this.radarChartPanels.some((row) => (row.chartData?.datasets || []).length > 1);
  },
  lastAssessmentDate() {
    const keys = Object.keys(this.resultsHistory);
    if (keys.length === 0) return null;
    return new Date(keys.sort().reverse()[0]); // 🔙 Последняя дата (по ключу)
  },
  todayDate() {
    return new Date();
  },
  recommendedNextAssessmentDate() {
    if (!this.lastAssessmentDate) return null;
    const nextDate = new Date(this.lastAssessmentDate);
    nextDate.setDate(nextDate.getDate() + 14); // 📅 через 2 недели
    return nextDate;
  }
},
watch: {
  rankedTrainingProgramIds() {
    this.ensureProgramFitExplanations();
  },
},
};
</script>

<style scoped>

.team-info-card {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  background: linear-gradient(135deg, #3f51b5, #caa6e8);
  border-radius: 12px;
  padding: 16px;
  color: #fff;
  text-align: center;
  gap: 8px;
  margin-bottom: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.info-block h3 {
  font-size: 14px;
  margin-bottom: 6px;
  font-weight: 500;
  opacity: 0.85;
}

.info-block p {
  font-size: 18px;
  font-weight: bold;
  margin: 0;
}

/* 🔹 Общий контейнер */
.results-container {
  max-width: 1300px;
  margin: auto;
  text-align: center;
  padding: 30px;
  background: #f4f6f9;
  font-family: 'Arial', sans-serif;
}

/* 🔹 Заголовок */
h1 {
  font-size: 30px;
  color: #2c3e50;
  font-weight: bold;
}

/* 🔹 Переключатель команд */
.team-switcher {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.switch-btn {
  font-size: 22px;
  padding: 8px 12px;
  background: #3498db;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  margin: 0 10px;
  transition: 0.2s;
}

.switch-btn:hover {
  background: #2980b9;
}

.team-name-container h2 {
  font-size: 24px;
  font-weight: bold;
  color: #2c3e50;
}

/* 🔹 Карточка общей оценки */
.team-info-card {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  background: linear-gradient(135deg, #3f51b5, #caa6e8);
  border-radius: 12px;
  padding: 16px;
  color: #fff;
  text-align: center;
  gap: 12px;
  margin-bottom: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  animation: fadeIn 0.4s ease;
}

.info-block {
  padding: 8px;
  border-radius: 8px;
  background-color: rgba(175, 87, 87, 0.08);
  transition: transform 0.2s ease;
}

.info-block:hover {
  transform: scale(1.03);
}

.info-block h3 {
  font-size: 14px;
  margin-bottom: 4px;
  opacity: 0.85;
}

.info-block p {
  font-size: 20px;
  font-weight: bold;
  margin: 0;
}

/* Цвета для оценки */
.score.danger {
  background-color: rgba(220, 53, 69, 0.3);
}
.score.warning {
  background-color: rgba(255, 138, 0, 0.81);
}
.score.info {
  background-color: rgba(23, 162, 184, 0.3);
}
.score.success {
  background-color: rgba(40, 167, 69, 0.3);
}

/* Цвета сравнения с рынком */
.market .better {
  color: #28a745;
}
.market .worse {
  color: #dc3545;
}

.previous-diff {
  display: block;
  font-size: 12px;
  margin-top: 4px;
  opacity: 0.8;
}

.team-info-card .info-block.market h3 {
  color: rgba(13, 23, 51, 0.9);
  opacity: 1;
}

.team-info-card .info-block.market .previous-diff {
  color: rgba(13, 23, 51, 0.78);
  opacity: 1;
}

/* Анимация */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}


/* 🔹 Графики */
.charts-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  justify-content: center;
  padding: 20px;
}

.radar-legend-hint {
  margin: -4px auto 14px;
  max-width: 920px;
  color: rgba(10, 20, 45, 0.6);
  font-size: 0.84rem;
  text-align: center;
}

.radar-chart {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 380px;
  height: 430px;
  background: white;
  border-radius: 15px;
  padding: 15px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}
.recommendations-block {
  margin-top: 30px;
  background: #f7f8fa;
  border: 2px solid #6e5fbe;
  padding: 20px;
  border-radius: 12px;
}

.timeline-dot:hover .tooltip,
.tooltip:hover {
  transform: translateX(-50%) scale(1.1);
  opacity: 1;
}

.recommendations-block ul {
  text-align: left;
  padding-left: 20px;
}

.timeline-modern {
  position: relative;
  margin: 40px 0;
  height: 80px;
  padding-top: 30px;
}

.timeline-track {
  position: relative;
  background-color: #ddd;
  height: 10px;
  border-radius: 3px;
  top: 30px;
  overflow: visible;
}

.timeline-fill {
  position: absolute;
  height: 100%;
  background-color: #4abebe;
  top: 0;
  left: 0;
  border-radius: 1px;
  z-index: 0; /* самая задняя */
  transition: width 0.4s ease;
}

.timeline-dot {
  position: absolute;
  top: -3px;
  transform: translateX(-50%);
  width: 14px;
  height: 14px;
  background-color: #007bff;
  border-radius: 50%;
  z-index: 2;
}

.timeline-arrow.right {
  position: absolute;
  top: -8px;
  transform: translateX(-50%);
  z-index: 3;
}
.timeline-dot,
.timeline-arrow {
  z-index: 3;
}
.timeline-arrow .arrow {
  font-size: 18px;
  color: #31a35a;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.tooltip {
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%) scale(1);
  background: #3497d9;
  color: white;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 13px;
  white-space: nowrap;
  z-index: 9999;
  transition: transform 0.2s ease, opacity 0.2s ease;
  opacity: 0.95;
}



.timeline-days-left {
  text-align: center;
  margin-top: 38px; /* было 16px — увеличим */
  font-size: 14px;
  color: #444;
  position: relative;
  z-index: 1;
}

.tooltip.always-visible {
  opacity: 1;
  visibility: visible;
}

.recommendations-block li {
  margin-bottom: 10px;
  font-size: 16px;
}

.editable-input {
  width: 100%;
  resize: vertical;
  font-size: 14px;
  padding: 6px 8px;
  border-radius: 5px;
  border: 1px solid #ccc;
  transition: all 0.2s ease;
  font-family: inherit;
  line-height: 1.4;
  background: #fff;
}
.editable-input:focus {
  outline: none;
  border-color: #6699ff;
  box-shadow: 0 0 4px rgba(102, 153, 255, 0.5);
}
.completed {
  text-decoration: line-through;
  opacity: 0.6;
}



.improvement-plan-block {
  margin-top: 16px;
  padding: 18px;
  background: linear-gradient(170deg, #ffffff, #f7faff);
  border-radius: 16px;
  border: 1px solid #dbe5f3;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.08);
}

.plan-editable ul,
.improvement-plan-block ul {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  padding: 0;
  list-style: none;
  justify-content: flex-start;
}

.plan-editable .ar-plan-list {
  flex-direction: column;
  flex-wrap: nowrap;
  gap: 12px;
  max-width: 100%;
}

.plan-editable .ar-plan-item--editable {
  flex: none;
  max-width: none;
  min-width: auto;
}

.plan-editable li,
.improvement-plan-block li {
  flex: 1 1 220px;
  max-width: 280px;
  min-width: 200px;
  background: linear-gradient(180deg, #ffffff, #f8fbff);
  border: 1px solid #dbe5f3;
  border-radius: 14px;
  padding: 12px 12px 10px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 20px rgba(20, 43, 102, 0.08);
  position: relative;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.plan-editable li:hover,
.improvement-plan-block li:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 30px rgba(20, 43, 102, 0.14);
  z-index: 1;
}

.plan-editable textarea {
  resize: none;
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 8px;
  font-size: 14px;
  font-family: inherit;
  line-height: 1.4;
  width: 90%;
  overflow: hidden;
  min-height: 170px;
  transition: 0.2s ease;
}

.plan-editable textarea.completed {
  text-decoration: line-through;
  opacity: 0.6;
}

.plan-editable input[type="checkbox"] {
  align-self: flex-start;
  margin-bottom: 8px;
}



@media (max-width: 1200px) {
  .plan-editable li,
  .improvement-plan-block li {
    flex: 0 0 calc(33.333% - 16px) !important; /* 3 в ряд */
  }
}

@media (max-width: 800px) {
  .plan-editable li,
  .improvement-plan-block li {
    flex: 0 0 calc(50% - 16px) !important; /* 2 в ряд */
  }
}

@media (max-width: 768px) {
  .charts-container:not(.charts-container--shell) {
    grid-template-columns: 1fr !important;
    gap: 15px !important;
    padding: 15px 10px !important;
  }

  /* Легаси-страница /assessment-results: оставляем старое жёсткое
     поведение. На /new/assessment-results радары стилизованы через
     .charts-container--shell .radar-chart выше. */
  .charts-container:not(.charts-container--shell) .radar-chart {
    width: 100% !important;
    max-width: 320px !important;
    height: 400px !important;
    margin: 0 auto 15px auto !important;
    padding: 12px !important;
  }
  
  .team-info-card {
    grid-template-columns: 1fr !important;
    gap: 15px !important;
    padding: 20px 15px !important;
  }
  
  .info-block {
    padding: 10px !important;
    font-size: 14px !important;
  }
  
  .info-block h3 {
    font-size: 16px !important;
    margin-bottom: 5px !important;
  }
  
  .timeline-modern {
    display: none !important;
  }
  
  .timeline-track {
    height: 8px !important;
    margin: 0 10px !important;
  }
  
  .timeline-dot {
    width: 12px !important;
    height: 12px !important;
  }
  
  .tooltip {
    font-size: 12px !important;
    padding: 4px 8px !important;
    max-width: 200px !important;
    white-space: normal !important;
    word-wrap: break-word !important;
  }
  
  .timeline-previous-dates {
    font-size: 16px !important;
    margin-top: 8px !important;
    line-height: 1.4 !important;
    padding: 0 10px !important;
  }
  
  .timeline-days-left {
    font-size: 16px !important;
    margin-top: 15px !important;
  }
  
  .recommendations-block {
    margin: 20px 10px !important;
    padding: 15px !important;
    width: calc(100% - 20px) !important;
    box-sizing: border-box !important;
    overflow-wrap: break-word !important;
  }
  
  .recommendations-block ul {
    padding-left: 15px !important;
    margin: 0 !important;
  }
  
  .recommendations-block li {
    font-size: 16px !important;
    line-height: 1.5 !important;
    margin-bottom: 12px !important;
    word-wrap: break-word !important;
  }
  
  .improvement-plan-block {
    margin: 20px 10px !important;
    padding: 15px !important;
    width: calc(100% - 20px) !important;
    box-sizing: border-box !important;
  }
  
  .plan-editable ul,
  .improvement-plan-block ul {
    width: 100% !important;
    padding: 0 !important;
    margin: 0 !important;
  }
  
  .plan-editable li,
  .improvement-plan-block li {
    flex: 1 1 100% !important; /* 1 в ряд на мобильных */
    max-width: 100% !important;
    min-width: auto !important;
    margin-bottom: 10px !important;
  }
  
  .plan-editable textarea {
    width: 100% !important;
    font-size: 16px !important;
    box-sizing: border-box !important;
    min-height: 120px !important;
  }
  
  .plan-buttons {
    flex-direction: column !important;
    gap: 10px !important;
    width: 100% !important;
  }
  
  .ar-action-btn {
    width: 100% !important;
    min-width: auto !important;
    font-size: 16px !important;
    padding: 15px 20px !important;
  }
  
  .plan-cards {
    width: 100% !important;
    padding: 0 !important;
  }
  
  .plan-card {
    width: 100% !important;
    margin-bottom: 10px !important;
    padding: 15px !important;
    box-sizing: border-box !important;
  }
}

@media (max-width: 500px) {
  .plan-editable li,
  .improvement-plan-block li {
    flex: 1 1 100% !important; /* 1 в ряд */
  }
}

.plan-buttons {
  display: flex;
  gap: 12px;
  justify-content: flex-start;
  margin-top: 12px;
  flex-wrap: wrap;
}

.ar-action-btn {
  font-weight: 600;
  padding: 10px 20px;
  border-radius: 12px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, color 0.18s ease;
  font-size: 0.92rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 8px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
  min-width: 0;
  max-width: 100%;
}

.ar-action-btn:hover {
  transform: none;
  filter: none;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.1);
}

.ar-action-btn:disabled {
  opacity: 0.66;
  cursor: not-allowed;
  transform: none;
}

.ar-action-btn--primary {
  background: linear-gradient(135deg, rgba(32, 90, 255, 0.92), rgba(37, 99, 235, 0.95));
  border-color: rgba(32, 90, 255, 0.35);
  color: #fff;
}

.ar-action-btn--primary:hover:not(:disabled) {
  filter: brightness(1.03);
}

.ar-action-btn--success {
  background: linear-gradient(135deg, #0d9488, #14b8a6);
  border-color: rgba(13, 148, 136, 0.35);
  color: #fff;
}

.ar-action-btn--success:hover:not(:disabled) {
  filter: brightness(1.03);
}

.ar-action-btn--ghost {
  background: #fff;
  border-color: rgba(10, 20, 45, 0.12);
  color: #334155;
  box-shadow: none;
}

.ar-action-btn--ghost:hover:not(:disabled) {
  background: rgba(248, 250, 252, 0.95);
  border-color: rgba(10, 20, 45, 0.18);
  color: #0f172a;
}
.info-block.level {
  position: relative;
  cursor: help;
}

.level-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.level-title {
  display: flex;
  align-items: center;
  gap: 6px;
}

.level-tooltip-container {
  position: relative;
  cursor: help;
}

.level-tooltip-icon {
  font-size: 14px;
  color: #fff;
  background-color: #888;
  border-radius: 50%;
  padding: 2px 6px;
  font-weight: bold;
  line-height: 1;
}

.level-tooltip-text {
  display: none;
  position: absolute;
  top: 28px;
  right: 0;
  background-color: #fff;
  color: #333;
  border: 1px solid #ccc;
  border-radius: 8px;
  padding: 10px 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  width: 260px;
  font-size: 13px;
  z-index: 99;
}

.level-tooltip-container:hover + .level-tooltip-text {
  display: block;
}
.info-icon {
  margin-left: 6px;
  cursor: pointer;
  font-size: 14px;
}
.timeline-previous-dates {
  text-align: center;
  margin-top: 4px;
  font-size: 13px;
  color: #555;
}

/* —— /new assessment results: sv-card + trainings (matches UserSurvey shell) */
.sv-card {
  border-radius: 18px;
  border: 1px solid rgba(10, 20, 45, 0.08);
  background: linear-gradient(175deg, rgba(255, 255, 255, 0.96), rgba(246, 249, 255, 0.9));
  box-shadow: 0 18px 56px rgba(10, 20, 45, 0.09);
  padding: 20px 18px 22px;
  margin-bottom: 16px;
  text-align: left;
}

.sv-card--muted {
  background: rgba(255, 255, 255, 0.88);
}

.ar-section-metrics .team-info-card {
  margin-bottom: 0;
}

.charts-container--shell {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 18px;
  padding: 0;
  max-width: 1100px;
  margin: 0 auto 12px;
}

/* sv-card-стиль для карточек радаров на /new/assessment-results
   (специфичность выше, чем у легаси-правила .radar-chart, поэтому именно
    эти значения применятся, перекрывая старые white/15px/4px shadow). */
.charts-container--shell .radar-chart {
  width: 100%;
  max-width: none;
  height: auto;
  min-height: 380px;
  margin: 0;
  border-radius: 18px;
  border: 1px solid rgba(10, 20, 45, 0.08);
  background: linear-gradient(175deg, rgba(255, 255, 255, 0.96), rgba(246, 249, 255, 0.9));
  box-shadow: 0 18px 56px rgba(10, 20, 45, 0.09);
  padding: 16px 16px 18px;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  align-items: stretch;
  justify-content: flex-start;
}

.charts-container--shell .radar-chart:hover {
  transform: translateY(-2px);
  box-shadow: 0 22px 64px rgba(10, 20, 45, 0.12);
}

@media (max-width: 600px) {
  .charts-container--shell {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .charts-container--shell .radar-chart {
    min-height: 340px;
    padding: 14px 12px 16px;
  }
}

.ar-persist-wrap .improvement-plan-block,
.ar-persist-wrap .recommendations-block {
  margin-top: 12px;
  box-shadow: none;
}

.ar-persist-wrap .improvement-plan-block:first-child {
  margin-top: 0;
}

/* ── Plan / recommendations hub (/new shell) ───────────────────────────── */
.ar-persist-hub {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.ar-persist-hub__head {
  margin-bottom: 18px;
  padding-bottom: 14px;
  border-bottom: 1px solid rgba(10, 20, 45, 0.08);
}

.ar-persist-hub__title {
  margin: 0 0 6px;
  font-size: 1.12rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: rgba(10, 20, 45, 0.92);
}

.ar-persist-hub__subtitle {
  margin: 0;
  font-size: 0.88rem;
  line-height: 1.5;
  color: #64748b;
  max-width: 640px;
}

.ar-persist-hub__step {
  margin: 0 0 10px;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
}

.ar-plan-section .ar-action-btn--primary {
  min-width: auto;
  align-self: flex-start;
}

.ar-persist-status {
  margin-top: 10px;
  font-size: 0.9rem;
  color: #475569;
}

.ar-persist-divider {
  height: 1px;
  margin: 22px 0;
  background: linear-gradient(90deg, transparent, rgba(10, 20, 45, 0.1), transparent);
}

.ar-persist-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
}

.ar-persist-toolbar--right {
  justify-content: flex-end;
}

.ar-action-btn--secondary-cta {
  min-width: 0;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
}

.ar-plan-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  list-style: none;
  padding: 0;
  margin: 14px 0 0;
  width: 100%;
}

.ar-plan-item {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid rgba(10, 20, 45, 0.08);
  border-left: 4px solid rgba(32, 90, 255, 0.55);
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 8px 24px rgba(10, 20, 45, 0.06);
  text-align: left;
  max-width: 100%;
}

.improvement-plan-block li.ar-plan-item {
  flex: none !important;
  max-width: 100% !important;
  min-width: auto !important;
  flex-direction: row !important;
}

.ar-plan-item--editable {
  position: relative;
  padding-right: 36px;
}

.ar-plan-check {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-top: 4px;
  cursor: pointer;
}

.ar-plan-text {
  flex: 1;
  font-size: 0.92rem;
  line-height: 1.5;
  color: #1e293b;
  word-break: break-word;
}

.ar-plan-textarea {
  flex: 1;
  min-height: 96px;
  width: 0;
  box-sizing: border-box;
  border-radius: 10px;
  border: 1px solid rgba(10, 20, 45, 0.12);
  padding: 10px 12px;
  font-family: inherit;
  font-size: 0.9rem;
  line-height: 1.45;
  resize: vertical;
  background: rgba(248, 250, 255, 0.85);
}

.ar-plan-remove {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #94a3b8;
  font-size: 1.4rem;
  font-weight: 300;
  line-height: 1;
  cursor: pointer;
  opacity: 0.65;
  transition: opacity 0.15s ease, background 0.15s ease, color 0.15s ease;
}

.ar-plan-remove:hover {
  opacity: 1;
  background: rgba(15, 23, 42, 0.06);
  color: #475569;
}

.ar-rec-body {
  margin-top: 14px;
  padding: 16px 18px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(10, 20, 45, 0.07);
  font-size: 0.92rem;
  line-height: 1.65;
  color: #334155;
  max-width: 100%;
}

.ar-rec-body :deep(p) {
  margin: 0 0 0.75em;
}

.ar-rec-body :deep(p:last-child) {
  margin-bottom: 0;
}

.ar-persist-hub .improvement-plan-block,
.ar-persist-hub .recommendations-block {
  margin-top: 0;
  padding: 0;
  background: transparent;
  border: none;
  box-shadow: none;
}

.trn-rec--footer {
  margin-top: 8px;
  margin-bottom: 8px;
}

.auth-notice--shell {
  max-width: 640px;
  margin-left: auto;
  margin-right: auto;
}

/* ── Recommended training programs (cards) ───────────────────────────── */
.trn-rec {
  margin: 28px auto 24px;
  max-width: 1200px;
  padding: 0 4px;
}

.trn-rec__head {
  margin-bottom: 16px;
  text-align: center;
}

.trn-rec__title {
  margin: 0 0 6px;
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #0f172a;
}

.trn-rec__lead {
  margin: 0 auto;
  color: #64748b;
  font-size: 0.92rem;
  line-height: 1.55;
  max-width: 720px;
}

.trn-rec__hint {
  margin: 14px 0 0;
  font-size: 0.82rem;
  color: #64748b;
}

.trn-rec__grid {
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  max-width: 920px;
  margin: 0 auto;
}

.trn-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 22px 22px 20px;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.trn-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 14px 32px rgba(15, 23, 42, 0.08);
  transform: translateY(-2px);
}

.trn-card__head { display: grid; gap: 8px; }

.trn-card__online {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background: #dcfce7;
  border: 1px solid #bbf7d0;
  color: #166534;
  font-size: 0.74rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  width: fit-content;
}

.trn-card__online::before {
  content: "";
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  flex-shrink: 0;
}

.trn-card__title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.35;
}

.trn-card__pitch {
  margin: 0;
  font-size: 0.9rem;
  color: #475569;
  line-height: 1.55;
}

.trn-card__outcomes {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 6px;
}

.trn-card__outcomes li {
  position: relative;
  padding-left: 20px;
  font-size: 0.86rem;
  line-height: 1.5;
  color: #334155;
}

.trn-card__outcomes li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.55em;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.18);
}

.trn-card__meta {
  display: grid;
  gap: 6px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
}

.trn-card__meta-row {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 0.84rem;
}

.trn-card__meta-label { color: #64748b; }

.trn-card__meta-val { color: #0f172a; font-weight: 600; }

.trn-card__fit {
  border-radius: 12px;
  padding: 12px 14px;
  background: rgba(99, 102, 241, 0.07);
  border: 1px solid rgba(99, 102, 241, 0.18);
  display: grid;
  gap: 4px;
}

.trn-card__fit--loading {
  background: rgba(148, 163, 184, 0.1);
  border-color: rgba(148, 163, 184, 0.22);
  color: #475569;
  font-size: 0.84rem;
  line-height: 1.5;
}

.trn-card__fit-label {
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #4f46e5;
}

.trn-card__fit-body {
  margin: 0;
  font-size: 0.86rem;
  line-height: 1.55;
  color: #1e293b;
}

.trn-card__actions {
  margin-top: auto;
}

.trn-card__actions.yes-no {
  gap: 0.75rem;
}

.trn-card__actions .btn-answer {
  flex: 1 1 140px;
  text-align: center;
  box-sizing: border-box;
}

.trn-card__actions a.btn-answer {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* Как в TakeMaturity.vue: ряд «Да / Нет» для действий по программе */
.yes-no {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.btn-answer {
  padding: 0.6rem 1.25rem;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  border: 2px solid #d1d5db;
  background: #fff;
  transition: all 0.2s;
  font-family: inherit;
  font-size: 0.9rem;
}

.btn-yes.active {
  background: #10b981;
  border-color: #10b981;
  color: #fff;
}

.btn-no.active {
  background: #ef4444;
  border-color: #ef4444;
  color: #fff;
}

.btn-answer:hover:not(.active) {
  border-color: #9ca3af;
  background: #f3f4f6;
}

/* ── Lead modal (pop-up, not inline) ─────────────────────── */
.lead-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
  animation: leadModalFadeIn 0.18s ease-out;
}

@keyframes leadModalFadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}

.lead-modal {
  position: relative;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  padding: 28px 24px 22px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.22);
  font-family: inherit;
  color: #0f172a;
  max-height: 90vh;
  overflow-y: auto;
}

.lead-modal__title {
  margin: 0 0 6px;
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #0f172a;
}

.lead-modal__hint {
  margin: 0 0 18px;
  font-size: 0.82rem;
  color: #64748b;
  line-height: 1.5;
}

.lead-modal__program {
  margin: -8px 0 16px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(32, 90, 255, 0.06);
  border: 1px solid rgba(32, 90, 255, 0.15);
  font-size: 0.88rem;
  line-height: 1.45;
  color: #1e293b;
}

.lead-modal__program-label {
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 4px;
}

.lead-modal__form {
  display: grid;
  gap: 14px;
}

.lead-modal__row {
  display: grid;
  gap: 6px;
}

.lead-modal__label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #334155;
  letter-spacing: 0.01em;
}

.lead-modal__input {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 0.92rem;
  font-family: inherit;
  background: #ffffff;
  color: #0f172a;
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.lead-modal__input:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.18);
}

.lead-modal__input--textarea {
  resize: vertical;
  min-height: 76px;
  line-height: 1.45;
}

.lead-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 4px;
  flex-wrap: wrap;
}

.lead-modal__btn {
  border-radius: 10px;
  padding: 10px 18px;
  font-size: 0.92rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    box-shadow 0.15s ease, transform 0.15s ease;
  min-height: unset;
  line-height: 1.2;
}

.lead-modal__btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.lead-modal__btn--primary {
  background: #6366f1;
  color: #ffffff;
  border-color: #6366f1;
}

.lead-modal__btn--primary:hover:not(:disabled) {
  background: #4f46e5;
  border-color: #4f46e5;
  box-shadow: 0 6px 14px rgba(99, 102, 241, 0.28);
}

.lead-modal__btn--ghost {
  background: #ffffff;
  color: #334155;
  border-color: #e2e8f0;
}

.lead-modal__btn--ghost:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.lead-modal__status {
  margin: 4px 0 0;
  color: #0f766e;
  font-size: 0.82rem;
}

</style>

